import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useQuestionnaireSteps } from '@/hooks/useQuestionnaireSteps';
import { QuestionnaireData } from '@/types/category';
import { ProgressIndicator } from '@/components/ProgressIndicator';
import { PersonalInfoStep } from './steps/PersonalInfoStep';
import { SessionDetailsStep } from './steps/SessionDetailsStep';
import { VoicePreferencesStep } from './steps/VoicePreferencesStep';
import { ReviewStep } from './steps/ReviewStep';
import { educationalSessionSchema } from '@/utils/validationSchemas';
import { useToast } from '@/hooks/use-toast';
import { sanitizeEducationalInput } from '@/lib/utils';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';

interface MultiStepQuestionnaireProps {
  categoryId: string;
  categoryName: string;
  categoryConfig: any;
  hasBiography: boolean;
  sessionType: 'individual' | 'group';
  groupSession?: any; // GroupSession type
  isNameDisabled?: boolean;
  isGenerating: boolean;
  isPractitionerMode?: boolean;
  clientData?: any; // Client data for practitioner-created sessions
  initialData?: Partial<QuestionnaireData> | null; // Saved preferences for prefilling
  onSubmit: (data: QuestionnaireData) => void;
  onCancel: () => void;
}

export const MultiStepQuestionnaire: React.FC<MultiStepQuestionnaireProps> = ({
  categoryId,
  categoryName,
  categoryConfig,
  hasBiography,
  sessionType,
  groupSession,
  isNameDisabled = false,
  isGenerating,
  isPractitionerMode = false,
  clientData,
  initialData,
  onSubmit,
  onCancel
}) => {
  const {
    currentStep,
    currentStepId,
    steps,
    totalSteps,
    formData,
    updateFormData,
    nextStep,
    prevStep,
    goToStep,
    canGoNext,
    validateStep,
    isFirstStep,
    isLastStep
  } = useQuestionnaireSteps(categoryId, hasBiography, sessionType, !!groupSession, clientData, initialData, isPractitionerMode);

  const { freeSessionsRemaining } = usePractitionerCredits();

  const handleSubmit = () => {
    if (isLastStep && formData) {
      // Sanitize educational input before submission
      const sanitizedData = { ...formData };
      if (sanitizedData.customEducationalTopic) {
        sanitizedData.customEducationalTopic = sanitizeEducationalInput(sanitizedData.customEducationalTopic);
      }
      if (sanitizedData.groupCustomEducationalTopic) {
        sanitizedData.groupCustomEducationalTopic = sanitizeEducationalInput(sanitizedData.groupCustomEducationalTopic);
      }

      // Map form fields to expected backend fields
      if (sanitizedData.backgroundMusicSelection) {
        sanitizedData.backgroundMusic = sanitizedData.backgroundMusicSelection;
      }
      if (sanitizedData.specific_voice_id) {
        sanitizedData.voiceId = sanitizedData.specific_voice_id;
      }

      // Log voice and music selections for debugging
      console.log('🎵 Questionnaire submission - Voice:', {
        specific_voice_id: sanitizedData.specific_voice_id,
        voiceId: sanitizedData.voiceId,
        voiceGender: sanitizedData.voiceGender
      });
      console.log('🎵 Questionnaire submission - Music:', {
        backgroundMusicSelection: sanitizedData.backgroundMusicSelection,
        backgroundMusic: sanitizedData.backgroundMusic
      });

      onSubmit(sanitizedData as QuestionnaireData);
    }
  };

  const { toast } = useToast();

  const handleNext = () => {
    if (canGoNext()) {
      // Only validate educational sessions
      const isEducational = formData?.sessionType === 'Educational & Learning Session' ||
                           formData?.groupSessionType === 'Group Educational & Learning Session';

      if (isEducational) {
        const validation = educationalSessionSchema.safeParse(formData);
        if (!validation.success) {
          const errors = validation.error.errors.map(e => e.message);
          toast({
            title: "Validation Error",
            description: errors.join(', '),
            variant: "destructive"
          });
          return;
        }
      }

      if (isLastStep) {
        handleSubmit();
      } else {
        nextStep();
      }
    }
  };

  // Get validation info for current step
  const validation = validateStep(currentStepId);

  // Map step IDs to display names
  const stepTitles: Record<string, string> = {
    personal: 'Personal Info',
    details: 'Session Details',
    voice: 'Voice & Length',
    review: 'Review'
  };

  const progressSteps = steps.map((stepId, index) => ({
    title: stepTitles[stepId],
    isCompleted: index < currentStep,
    isCurrent: index === currentStep
  }));

  const renderCurrentStep = () => {
    switch (currentStepId) {
      case 'personal':
        return (
          <PersonalInfoStep
            formData={formData}
            onUpdate={updateFormData}
            sessionType={sessionType}
            isNameDisabled={isNameDisabled}
            groupName={groupSession?.name || groupSession?.group_name}
          />
        );
      case 'details':
        return (
          <SessionDetailsStep
            formData={formData}
            onUpdate={updateFormData}
            categoryConfig={categoryConfig}
            sessionType={sessionType}
            categoryId={categoryId}
            isPractitionerMode={isPractitionerMode}
          />
        );
      case 'voice':
        return (
          <VoicePreferencesStep
            formData={formData}
            onUpdate={updateFormData}
            categoryId={categoryId}
            freeSessionsAvailable={freeSessionsRemaining}
          />
        );
      case 'review':
        return (
          <ReviewStep
            formData={formData}
            categoryName={categoryName}
            onEdit={goToStep}
            sessionType={sessionType}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8">
        <ProgressIndicator
          steps={progressSteps}
          currentStep={currentStep}
          variant="horizontal"
        />
      </div>

      {/* Main Content Card */}
      <Card className="border-2">
        <CardContent className="pt-6 pb-6">
          {/* Step Content - Fixed Height Container */}
          <div className="min-h-[400px] max-h-[500px] overflow-y-auto px-2">
            {renderCurrentStep()}
          </div>

          {/* Validation Feedback */}
          {!validation.isValid && !isLastStep && (
            <div className="mt-6 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300 font-medium mb-1">
                Required fields missing:
              </p>
              <ul className="text-xs text-amber-600 dark:text-amber-400 list-disc list-inside">
                {validation.missingFields.map((field, index) => (
                  <li key={index}>{field}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-6 pt-6 border-t">
            <Button
              variant="ghost"
              onClick={isFirstStep ? onCancel : prevStep}
              disabled={isGenerating}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {isFirstStep ? 'Cancel' : 'Back'}
            </Button>

            <div className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {totalSteps}
            </div>

            <Button
              onClick={handleNext}
              disabled={!canGoNext() || isGenerating}
              className="gap-2"
            >
              {isLastStep ? (
                <>
                  {isGenerating ? 'Generating...' : 'Create Session'}
                  <Check className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step Info Footer */}
      <div className="mt-4 text-center text-sm text-muted-foreground">
        {!isLastStep && (
          <p>
            💡 All fields marked with <span className="text-destructive">*</span> are required to continue
          </p>
        )}
      </div>
    </div>
  );
};